import {
  Component, OnInit, OnDestroy, ViewChild, ElementRef,
  Output, EventEmitter, ChangeDetectorRef, NgZone
} from '@angular/core';
import { AppService } from 'src/app/services/app.service';
import { AppEnum } from 'src/app/appenum';

export interface CapturedVideo {
  id: string;
  buffer: ArrayBuffer;
  mimeType: string;
  duration: number;
  fileName: string;
}
@Component({
  selector: 'app-video-capture',
  templateUrl: './video-capture.component.html',
  styleUrls: ['./video-capture.component.scss'],
  standalone: false
})
export class VideoCaptureComponent implements OnInit, OnDestroy {

  @ViewChild('videoPreview', { static: false }) videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoPlayback', { static: false }) playbackRef!: ElementRef<HTMLVideoElement>;
  @Output() videoReady = new EventEmitter<CapturedVideo>();
  @Output() captureCancelled = new EventEmitter<string>();

  // stream & recorder
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];

  // timers
  private recordingTimer: any = null;
  private countdownTimer: any = null;

  // state
  facingMode: 'user' | 'environment' = 'environment';
  isRecording = false;
  isProcessing = false;
  isCameraReady = false;
  hasPreview = false;
  cameraError = '';

  // timing
  elapsed = 0;       // seconds recorded so far
  countdown = 0;       // seconds remaining
  readonly MAX_DURATION = 15;

  // preview
  previewUrl: string | null = null;
  previewDuration = 0;

  // supported mime type resolved at runtime
  private resolvedMime = 'video/webm';

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private _appServices: AppService,
    private _appenum: AppEnum
  ) { }

  async ngOnInit(): Promise<void> {
    await this.startCamera();
  }

  ngOnDestroy(): void {
    this.stopRecordingCleanup();
    this.stopStream();
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

  // Camera lifecycle

  async startCamera(): Promise<void> {
    this.stopStream();
    this.cameraError = '';
    this.isCameraReady = false;
    this.cdr.detectChanges();

    const constraints: MediaStreamConstraints = {
      video: {
        facingMode: this.facingMode,
        width: { ideal: 1280 },
        height: { ideal: 720 }
      },
      audio: true
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      this._log('camera_start', null, `Camera started | facing: ${this.facingMode}`);

      // give the DOM a tick so videoRef is available
      setTimeout(() => {
        if (this.videoRef?.nativeElement) {
          this.videoRef.nativeElement.srcObject = this.stream;
          this.videoRef.nativeElement.muted = true;
          this.videoRef.nativeElement.play().catch(err => {
            this._logError('camera_play', err, 'Video element play() failed');
          });
          this.isCameraReady = true;
          this.cdr.detectChanges();
        }
      }, 100);

    } catch (err: any) {
      this.cameraError = this.friendlyError(err);
      this._logError('camera_start', err, 'getUserMedia failed');
      this.cdr.detectChanges();
    }
  }

  async flipCamera(): Promise<void> {
    if (this.isRecording) return;
    this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
    this._log('camera_flip', null, `Flipping to facing: ${this.facingMode}`);
    await this.startCamera();
  }

  private stopStream(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
    if (this.videoRef?.nativeElement) {
      this.videoRef.nativeElement.srcObject = null;
    }
  }

  // Recording
  startRecording(): void {
    if (!this.stream || this.isRecording) return;

    this.chunks = [];
    this.elapsed = 0;
    this.countdown = this.MAX_DURATION;

    this.resolvedMime = this.getSupportedMimeType();

    try {
      this.recorder = new MediaRecorder(this.stream, { mimeType: this.resolvedMime });
    } catch {
      this.recorder = new MediaRecorder(this.stream);
      this.resolvedMime = this.recorder.mimeType || 'video/webm';
    }

    this.recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data?.size > 0) this.chunks.push(e.data);
    };

    this.recorder.onstop = () => this.onRecorderStop();

    this.recorder.start(200);
    this.isRecording = true;
    this.cdr.detectChanges();

    this._log('recording_start', null, 'Recording started');

    this.recordingTimer = setInterval(() => {
      this.ngZone.run(() => {
        this.elapsed++;
        this.countdown = this.MAX_DURATION - this.elapsed;
        this.cdr.detectChanges();

        if (this.elapsed >= this.MAX_DURATION) {
          this._log('recording_limit', null, '15s limit reached — stopping automatically');
          this.stopRecording();
        }
      });
    }, 1000);
  }

  stopRecording(): void {
    if (!this.isRecording || !this.recorder) return;
    this._log('recording_stop', null, `Stopping recording at ${this.elapsed}s`);
    this.recorder.stop();
    this.stopRecordingCleanup();
    this.isRecording = false;
    this.isProcessing = true;
    this.cdr.detectChanges();
  }

  private stopRecordingCleanup(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private async onRecorderStop(): Promise<void> {
    if (this.chunks.length === 0) {
      this._log('recording_stop', 'NO_DATA', 'Recorder stopped but no chunks collected');
      this.isProcessing = false;
      this.cdr.detectChanges();
      return;
    }

    try {
      const blob = new Blob(this.chunks, { type: this.resolvedMime });
      const buffer = await blob.arrayBuffer();
      this.chunks.length = 0;

      const duration = this.elapsed;
      const fileName = `video_${Date.now()}.webm`;
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
      this.previewUrl = URL.createObjectURL(blob);
      this.previewDuration = duration;
      this.hasPreview = true;

      // store on instance for useVideo()
      this._pendingVideo = { id: crypto.randomUUID(), buffer, mimeType: this.resolvedMime, duration, fileName };

      this._log('recording_processed', null,
        `Video processed | size: ${buffer.byteLength} bytes | duration: ${duration}s`);

      this.stopStream();

    } catch (err: any) {
      this._logError('recording_process', err, 'Failed to process recorded video');
    } finally {
      this.isProcessing = false;
      this.ngZone.run(() => this.cdr.detectChanges());
    }
  }

  // Preview actions

  private _pendingVideo: CapturedVideo | null = null;

  useVideo(): void {
    if (!this._pendingVideo || this.isProcessing) return;
    this.isProcessing = true;
    this.cdr.detectChanges();
    this.videoReady.emit(this._pendingVideo);
  }

  retake(): void {
    // free pending video
    if (this._pendingVideo) {
      this._pendingVideo.buffer = null as any;
      this._pendingVideo = null;
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
    this.hasPreview = false;
    this.elapsed = 0;
    this.countdown = 0;
    this.isProcessing = false;
    this.startCamera();
  }

  cancel(): void {
    this.stopRecordingCleanup();
    this.stopStream();
    if (this._pendingVideo) {
      this._pendingVideo.buffer = null as any;
      this._pendingVideo = null;
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
    this.captureCancelled.emit('true');
  }

  // called by parent after save is complete
  resetProcessing(): void {
    this.isProcessing = false;
    this.cdr.detectChanges();
  }

  // Helpers

  get progressPercent(): number {
    return (this.elapsed / this.MAX_DURATION) * 100;
  }

  get formattedElapsed(): string {
    return this.formatTime(this.elapsed);
  }

  get formattedCountdown(): string {
    return this.formatTime(this.countdown);
  }

  private formatTime(s: number): string {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  private getSupportedMimeType(): string {
    const types = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ];
    for (const t of types) {
      if (MediaRecorder.isTypeSupported(t)) return t;
    }
    return '';
  }

  private friendlyError(err: any): string {
    if (err?.name === 'NotAllowedError') return 'Camera permission denied. Please allow camera access.';
    if (err?.name === 'NotFoundError') return 'No camera found on this device.';
    if (err?.name === 'NotReadableError') return 'Camera is in use by another app.';
    return 'Could not access camera. Please try again.';
  }

  private _log(event: string, code: string | null, msg: string): void {
    this._appServices?.logsActivity?.(
      this._appenum?.EntityOfLogLevel?.Info,
      msg, 'VideoCaptureComponent'
    );
  }

  private _logError(event: string, err: any, msg: string): void {
    this._appServices?.logsActivity?.(
      this._appenum?.EntityOfLogLevel?.Error,
      `${msg} | ${err?.message}`, 'VideoCaptureComponent'
    );
  }

}
